import { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';

const profileSchema = z.object({
    nomeFantasia: z.string().min(2, 'Nome fantasia é obrigatório'),
    cnpj: z.string().optional().refine((val) => {
        if (!val) return true;
        // Simple regex for valid structure or raw numbers length
        const clean = val.replace(/\D/g, '');
        return clean.length === 14;
    }, 'CNPJ deve ter 14 dígitos'),
    email: z.string().email('Email inválido').optional().or(z.literal('')),
    logoKey: z.string().optional() // We store the MinIO key
});

type ProfileFormData = z.infer<typeof profileSchema>;

export function CompanyProfile() {
    const [loading, setLoading] = useState(true);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [logoPreview, setLogoPreview] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors, isSubmitting }
    } = useForm<ProfileFormData>({
        resolver: zodResolver(profileSchema)
    });

    // Helper for authenticated requests
    const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
        const token = localStorage.getItem('auth_token');
        const tenantId = localStorage.getItem('selected_tenant_id');
        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        if (token) {
            (headers as Record<string, string>).Authorization = `Bearer ${token}`;
        }
        if (tenantId) {
            (headers as Record<string, string>)['X-Tenant-Id'] = tenantId;
        }

        return fetch(url, {
            ...options,
            headers,
        });
    }, []);

    const loadProfile = useCallback(async () => {
        try {
            const response = await authenticatedFetch('/api/company-profile');
            if (response.ok) {
                const data = await response.json();
                setValue('nomeFantasia', data.nomeFantasia || '');
                setValue('cnpj', data.cnpj || '');
                setValue('email', data.email || '');
                setValue('logoKey', data.logoKey || '');

                if (data.logoUrl) {
                    setLogoPreview(data.logoUrl);
                }
            }
        } catch (error) {
            console.error('Error loading profile:', error);
            toast.error('Erro ao carregar perfil da empresa');
        } finally {
            setLoading(false);
        }
    }, [authenticatedFetch, setValue]);

    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type and size (e.g. max 2MB)
        if (!file.type.startsWith('image/')) {
            toast.error('Por favor, selecione uma imagem');
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            toast.error('A imagem deve ter no máximo 2MB');
            return;
        }

        setUploadingLogo(true);
        try {
            // 1. Get Presigned URL
            const presignedResponse = await authenticatedFetch('/api/company-profile/upload-url', {
                method: 'POST',
                body: JSON.stringify({
                    filename: file.name,
                    type: file.type
                })
            });

            if (!presignedResponse.ok) {
                throw new Error('Falha ao gerar URL de upload');
            }

            const { uploadUrl, key } = await presignedResponse.json();

            // 2. Upload to MinIO
            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type
                }
            });

            if (!uploadResponse.ok) {
                throw new Error('Falha ao enviar arquivo para armazenamento');
            }

            // 3. Update State
            setValue('logoKey', key);
            // Optimize preview: use URL.createObjectURL or the returned accessUrl if public
            // For immediate feedback
            setLogoPreview(URL.createObjectURL(file));

            toast.success('Logo enviado com sucesso');

        } catch (error) {
            console.error('Logo upload error:', error);
            toast.error('Erro ao enviar logo');
        } finally {
            setUploadingLogo(false);
        }
    };

    const onSubmit = async (data: ProfileFormData) => {
        try {
            const response = await authenticatedFetch('/api/company-profile', {
                method: 'PUT',
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                throw new Error('Falha ao atualizar perfil');
            }

            const updated = await response.json();
            // Update preview with secured URL from backend
            if (updated.logoUrl) {
                setLogoPreview(updated.logoUrl);
            }

            toast.success('Perfil atualizado com sucesso!');
        } catch (error) {
            console.error('Update error:', error);
            toast.error('Erro ao salvar alterações');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center p-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="animate-fadeIn h-full">
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl p-8 h-full flex flex-col relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 rounded-full blur-3xl -mr-32 -mt-32"></div>
                
                <div className="mb-8 flex-shrink-0 relative z-10">
                    <h2 className="text-2xl font-bold font-oswald text-white flex items-center gap-4 uppercase tracking-tight">
                        <span className="p-3 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-2xl border border-blue-500/30 shadow-lg">🏢</span>
                        Perfil da Empresa
                    </h2>
                    <p className="text-gray-400 mt-2 text-sm max-w-2xl">Gerencie as informações fundamentais da sua organização para personalização do sistema.</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-full flex-1 flex flex-col relative z-10">
                    {/* Logo Section - Premium Style */}
                    <div className="flex flex-col md:flex-row gap-8 p-6 bg-white/5 rounded-2xl border border-white/10 flex-shrink-0 group hover:bg-white/10 transition-all duration-500">
                        <div className="relative group/logo flex-shrink-0">
                            <div className="w-32 h-32 rounded-2xl bg-white/5 border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden shadow-inner group-hover/logo:border-blue-500/50 transition-colors duration-500">
                                {logoPreview ? (
                                    <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-2" />
                                ) : (
                                    <span className="text-5xl opacity-20 grayscale group-hover/logo:grayscale-0 transition-all duration-500">🖼️</span>
                                )}

                                {uploadingLogo && (
                                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="absolute -bottom-2 -right-2 p-2.5 bg-blue-600 text-white rounded-xl shadow-xl hover:bg-blue-500 transition-all hover:scale-110 border border-blue-400/30"
                                title="Alterar Logo"
                            >
                                <span className="text-sm">📷</span>
                            </button>
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={handleLogoUpload}
                            />
                        </div>

                        <div className="flex-1 space-y-4">
                            <div>
                                <h3 className="font-bold font-oswald text-white text-lg uppercase tracking-wide">Logotipo da Empresa</h3>
                                <p className="text-xs text-gray-500 font-medium mt-1 uppercase tracking-widest">
                                    Recomendado: PNG ou JPG com fundo transparente (Máx 2MB).
                                </p>
                            </div>

                            <div className="grid grid-cols-1 gap-4">
                                <div>
                                    <label className="block text-[10px] font-bold font-oswald text-gray-400 mb-2 uppercase tracking-widest ml-1">
                                        Nome Fantasia *
                                    </label>
                                    <input
                                        {...register('nomeFantasia')}
                                        className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all placeholder-gray-600"
                                        placeholder="Minha Empresa Ltda"
                                    />
                                    {errors.nomeFantasia && <span className="text-red-400 text-[10px] font-bold uppercase tracking-widest mt-2 ml-1 flex items-center gap-1">⚠️ {errors.nomeFantasia.message}</span>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-shrink-0">
                        <div className="group">
                            <label className="block text-[10px] font-bold font-oswald text-gray-400 mb-2 uppercase tracking-widest ml-1 group-focus-within:text-blue-400 transition-colors">
                                CNPJ da Organização
                            </label>
                            <input
                                {...register('cnpj')}
                                onChange={(e) => {
                                    const raw = e.target.value.replace(/\D/g, '');
                                    let formatted = raw;
                                    if (raw.length > 12) {
                                        formatted = raw.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2}).*/, '$1.$2.$3/$4-$5');
                                    } else if (raw.length > 8) {
                                        formatted = raw.replace(/^(\d{2})(\d{3})(\d{3})(\d{0,4}).*/, '$1.$2.$3/$4');
                                    } else if (raw.length > 5) {
                                        formatted = raw.replace(/^(\d{2})(\d{3})(\d{0,3}).*/, '$1.$2.$3');
                                    } else if (raw.length > 2) {
                                        formatted = raw.replace(/^(\d{2})(\d{0,3})/, '$1.$2');
                                    }
                                    setValue('cnpj', formatted);
                                }}
                                maxLength={18}
                                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all placeholder-gray-600 font-mono"
                                placeholder="00.000.000/0001-00"
                            />
                            {errors.cnpj && <span className="text-red-400 text-[10px] font-bold uppercase tracking-widest mt-2 ml-1 flex items-center gap-1">⚠️ {errors.cnpj.message}</span>}
                        </div>

                        <div className="group">
                            <label className="block text-[10px] font-bold font-oswald text-gray-400 mb-2 uppercase tracking-widest ml-1 group-focus-within:text-blue-400 transition-colors">
                                Email de Contato Oficial
                            </label>
                            <input
                                {...register('email')}
                                type="email"
                                className="w-full px-5 py-4 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:bg-white/10 focus:ring-2 focus:ring-blue-600/50 focus:border-blue-600 transition-all placeholder-gray-600"
                                placeholder="contato@empresa.com"
                            />
                            {errors.email && <span className="text-red-400 text-[10px] font-bold uppercase tracking-widest mt-2 ml-1 flex items-center gap-1">⚠️ {errors.email.message}</span>}
                        </div>
                    </div>

                    <div className="flex justify-end pt-8 border-t border-white/10 mt-auto">
                        <button
                            type="submit"
                            disabled={isSubmitting || uploadingLogo}
                            className="px-10 py-4 bg-blue-600 text-white rounded-xl font-bold font-oswald uppercase tracking-widest text-sm hover:bg-blue-500 transition-all shadow-lg shadow-blue-900/40 hover:shadow-blue-900/60 hover:-translate-y-1 disabled:opacity-50 flex items-center gap-3 border border-blue-400/20"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>Salvando...</span>
                                </>
                            ) : (
                                <>
                                    <span>💾 SALVAR ALTERAÇÕES</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
